import React, { useState } from 'react'
import * as R from 'ramda'
import moment from 'moment'
import { Link } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import { Link as RouterLink } from 'react-router-dom'
import { PlanId } from 'commons/plan'

import { isEmptyOrNil } from 'libs/empty'
import appConfigs from 'app/configs/appConfigs'
import { useAppName } from 'commons/usePartner'
import { useIsAuthorized } from 'app/auth/authHooks'
import { formatDate, getDateAndTime } from 'commons/date'
import { usePlanAddManual } from 'commons/usePlanAddManual'
import { Routes } from 'app/services/routeService/routeService'
import { PlanBillingPaymentMethodFormDialog } from 'app/main/plansAndBilling/billingOverview/PlanBillingPaymentMethodFormDialog'
import {
  getPlanType,
  hasPlanEnded,
  PlanPriceType,
  isPlanFreeTierByPlanId,
  isPlanFreeTrialByPlanId,
} from 'commons/plan'
import {
  useIsPlaceExpired,
  useIsPlaceRestricted,
  useIsPlaceInGracePeriod,
} from 'commons/useIsPlaceRestricted'
import {
  BillingWarningContainer,
  BillingDowngradedWarningContainer,
} from 'app/components/WarningEndPlan'
import { useSelector } from 'react-redux'
import { path } from 'ramda'
import { getDateFromNow } from 'commons/date'
import { getLocale } from 'app/translations/intl-context'
import { usePlace } from 'commons/usePlace'

function BillingOverviewNotification(planBill) {
  const localLang = getLocale()

  const appName = useAppName()
  const isAuthorized = useIsAuthorized()
  const isPlaceExpired = useIsPlaceExpired()
  const [isPlanAddManual] = usePlanAddManual()
  const isPlaceRestricted = useIsPlaceRestricted()
  const isPlaceInGracePeriod = useIsPlaceInGracePeriod()
  const nextPlan = R.path(['planBill', 'nextSubscription', 'plan'], planBill)
  const subscription = R.path(['planBill', 'activeSubscription'], planBill)
  const plan = R.path(['planBill', 'activeSubscription', 'plan'], planBill)
  const pendingApproval = useSelector(
    path(['place', 'info', 'pendingApproval']),
  )

  const [isOpenPaymentDialog, setIsOpenPaymentDialog] = useState(false)
  const place = usePlace()

  const today = moment().utc()
  const hasPlanCanceled = R.prop('endsAt', subscription)
  const isAnnual = getPlanType(R.prop('id', plan)) === PlanPriceType.ANNUAL
  const nextBilling = R.prop('nextBillingDate', subscription)
  const expireDate = moment(R.prop('endsAt', subscription))
  const countExpireDate = expireDate.diff(today, 'days')
  const isThirtyDay = countExpireDate > 0 && countExpireDate <= 30
  const isSevenDay = countExpireDate > 0 && countExpireDate <= 7
  const isEnding = R.isNil(nextBilling) && R.isNil(nextPlan)
  const countDownAnnual = isAnnual && isThirtyDay
  const isShowCountDown = countDownAnnual || isSevenDay
  const isFreeTrial =
    place.activeSubscription.braintreePlan === PlanId.FREE_TRIAL

  const toChangePlan = Routes.billingchangePlan()

  const shouldShowRecurringNotification = (type, nextBillingDate) => {
    const minimumDayToShow =
      type === PlanPriceType.MONTHLY
        ? appConfigs.minimumDayDisplayRecurringNotificationMonthly
        : appConfigs.minimumDayDisplayRecurringNotificationAnnual

    return moment().isBetween(
      moment(nextBillingDate).subtract(minimumDayToShow, 'days'),
      moment(nextBillingDate),
    )
  }

  if (!isAuthorized || pendingApproval) {
    return null
  }

  if (isPlanAddManual && hasPlanEnded({ subscription })) {
    return (
      <BillingWarningContainer icon="error_outline">
        <FormattedMessage
          id="planBillingNotification.cash.expire.label"
          values={{
            b: (text) => <b>{text}</b>,
            l1: (text) => (
              <Link
                to={toChangePlan}
                component={RouterLink}
                className="font-bold underline"
              >
                {text}
              </Link>
            ),
            appName,
          }}
        />
      </BillingWarningContainer>
    )
  }

  if (isPlanAddManual && isShowCountDown && isEnding) {
    return (
      <BillingWarningContainer icon="error_outline">
        <FormattedMessage
          id="planBillingNotification.expiring.label"
          values={{
            countDay: countExpireDate,
            l1: (text) => (
              <Link
                to={toChangePlan}
                component={RouterLink}
                className="font-bold underline"
              >
                {text}
              </Link>
            ),
          }}
        />
      </BillingWarningContainer>
    )
  }

  if (!isFreeTrial && today.isSame(expireDate, 'day')) {
    return (
      <BillingWarningContainer icon="error_outline">
        <FormattedMessage
          id="planBillingNotification.expiring.today.label"
          values={{
            count: getDateFromNow(expireDate, localLang, true),
            l1: (text) => (
              <Link
                to={toChangePlan}
                component={RouterLink}
                className="font-bold underline"
              >
                {text}
              </Link>
            ),
          }}
        />
      </BillingWarningContainer>
    )
  }

  if (isPlanFreeTrialByPlanId(R.prop('id', plan))) {
    if (hasPlanEnded({ subscription })) {
      return (
        <BillingWarningContainer icon="error_outline">
          <FormattedMessage
            id="warningEndTrial.end.label"
            values={{
              newLine: <br />,
              b: (text) => <b>{text}</b>,
              l1: (text) => (
                <Link
                  to={toChangePlan}
                  component={RouterLink}
                  className="font-bold underline"
                >
                  {text}
                </Link>
              ),
              appName,
            }}
          />
        </BillingWarningContainer>
      )
    }

    return null
  }

  if (isPlanFreeTierByPlanId(R.prop('id', plan))) {
    return null
  }

  if (!isEmptyOrNil(nextPlan)) {
    const planName = R.prop('name', nextPlan)
    const { date } = getDateAndTime(
      nextBilling || R.prop('endsAt', subscription),
    )

    return (
      <BillingDowngradedWarningContainer icon="error_outline">
        <FormattedMessage
          id="planBillingNotification.downgrade.label"
          values={{
            date,
            planName,
            b: (text) => <b>{text}</b>,
          }}
        />
      </BillingDowngradedWarningContainer>
    )
  }

  if (hasPlanCanceled && R.not(isPlanAddManual)) {
    const { date } = getDateAndTime(R.prop('endsAt', subscription))
    return (
      <BillingWarningContainer icon="error_outline">
        <FormattedMessage
          id="planBillingAlertMessage.cancel.label"
          values={{
            date,
          }}
        />
      </BillingWarningContainer>
    )
  }

  if (isPlanAddManual) return null

  if (isPlaceInGracePeriod) {
    return (
      <>
        <BillingWarningContainer icon="error_outline">
          <FormattedMessage
            id="planBillingNotification.chargeFail.label"
            values={{
              date: formatDate(
                moment(nextBilling).add(appConfigs.gracePeriod, 'days'),
              ),
              b: (text) => <b>{text}</b>,
            }}
          />
        </BillingWarningContainer>
      </>
    )
  }

  if (isPlaceExpired) {
    return (
      <BillingWarningContainer icon="error_outline">
        <FormattedMessage
          id="planBillingNotification.expired.label"
          values={{
            l1: (text) => (
              <Link
                to={toChangePlan}
                component={RouterLink}
                className="font-bold underline"
              >
                {text}
              </Link>
            ),
          }}
        />
      </BillingWarningContainer>
    )
  }

  if (isPlaceRestricted) {
    return (
      <>
        <BillingWarningContainer icon="error_outline">
          <FormattedMessage
            id="planBillingNotification.restrict.label"
            values={{
              date: formatDate(
                moment(nextBilling).add(appConfigs.expiresPlanInDay, 'days'),
              ),
              l1: (text) => (
                <Link
                  onClick={() => setIsOpenPaymentDialog(true)}
                  component={RouterLink}
                  className="font-bold underline"
                >
                  {text}
                </Link>
              ),
            }}
          />
        </BillingWarningContainer>
        <PlanBillingPaymentMethodFormDialog
          isOpen={isOpenPaymentDialog}
          onClose={() => {
            setIsOpenPaymentDialog(false)
          }}
        />
      </>
    )
  }

  if (
    shouldShowRecurringNotification(
      getPlanType(R.prop('id', plan)),
      nextBilling,
    )
  ) {
    return (
      <BillingDowngradedWarningContainer icon="error_outline">
        <FormattedMessage
          id="planBillingNotification.recurring.label"
          values={{
            type: getPlanType(R.prop('id', plan)),
            price: R.prop('price', plan),
            date: formatDate(nextBilling),
            appName,
          }}
        />
      </BillingDowngradedWarningContainer>
    )
  }

  return null
}

export default BillingOverviewNotification
